import type { AppCommandResult, OpenedApp } from '@erp/rpc';
import {
  appStatePatch,
  commandStatusPatch,
  feedStatusPatch,
  settledCommandStatus,
  settledFeedErrors,
} from './appState';

type FeedErrors = OpenedApp['errors'];
export type CommandResponse =
  AppCommandResult | { status: 'transport_failed'; message: string };

export async function commandResponse(
  run: () => Promise<AppCommandResult>,
): Promise<CommandResponse> {
  try {
    return await run();
  } catch (error) {
    return {
      status: 'transport_failed',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

interface CommandSettlement {
  outcome: 'transport' | 'command' | 'tool' | 'refresh' | 'success';
  summary: unknown;
  patch: Record<string, unknown>;
  feedErrors: FeedErrors;
  /** The feeds whose reload failed although the write applied. */
  refreshWarnings: string[];
  notices: string[];
  result: unknown;
  error: string | null;
}

/** Compute the entire publication before notifying any store subscribers. */
export function reconcileCommand({
  command,
  response,
  currentErrors,
}: {
  command: string;
  response: CommandResponse;
  currentErrors: FeedErrors;
}): CommandSettlement {
  if (response.status !== 'succeeded') {
    return {
      outcome: response.status === 'transport_failed' ? 'transport' : 'command',
      summary: {
        error: response.message,
        serverOutcome: response.status === 'failed' ? response.outcome : null,
      },
      patch: commandStatusPatch(command, {
        running: false,
        result: null,
        error: response.message,
      }),
      feedErrors: currentErrors,
      refreshWarnings: [],
      notices: [response.message],
      result: null,
      error: response.message,
    };
  }
  const status = settledCommandStatus(response.result);
  return {
    outcome: status.error
      ? 'tool'
      : response.errors.length
        ? 'refresh'
        : 'success',
    summary: {
      error: status.error,
      refreshed: Object.keys(response.state),
      errors: response.errors,
    },
    patch: {
      ...appStatePatch(response.state, response.errors),
      ...feedStatusPatch(response.state, response.errors),
      ...commandStatusPatch(command, status),
    },
    feedErrors: settledFeedErrors(
      currentErrors,
      response.state,
      response.errors,
    ),
    refreshWarnings: response.errors.map((error) => error.key),
    notices: [
      ...(status.error ? [status.error] : []),
      ...response.errors.map((error) => `${error.key}: ${error.message}`),
    ],
    result: response.result,
    error: status.error,
  };
}
